import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { updateChangeRequest } from "api";
import type { ScheduleItemChangeRequest } from "types";

/** Convert HH:MM (from <input type="time">) to 12-hour format, e.g. "2:30pm" */
function fmt12h(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h >= 12 ? "pm" : "am"}`;
}

export function ChangeRequestRow({
  request,
  scheduleItemId,
}: {
  request:        ScheduleItemChangeRequest;
  scheduleItemId: number;
}) {
  const queryClient = useQueryClient();
  const invalidate  = () => queryClient.invalidateQueries({ queryKey: ["scheduleItems"] });

  const approve = useMutation({
    mutationFn: () => updateChangeRequest(scheduleItemId, request.id, "approved"),
    onSuccess:  invalidate,
  });

  const deny = useMutation({
    mutationFn: () => updateChangeRequest(scheduleItemId, request.id, "denied"),
    onSuccess:  invalidate,
  });

  const isPending = approve.isPending || deny.isPending;

  const isTimeChange   = request.change_type === "time_change";
  const isCancellation = request.change_type === "cancellation";
  const d              = request.details;

  const statusStyles: Record<typeof request.status, string> = {
    requested: "bg-amber-100 text-amber-700",
    approved:  "bg-green-100  text-green-700",
    denied:    "bg-red-100    text-red-600",
  };
  const statusLabels: Record<typeof request.status, string> = {
    requested: "Open",
    approved:  "Approved",
    denied:    "Denied",
  };

  return (
    <div
      className="flex items-center justify-between gap-3 text-xs"
      onClick={(e) => e.stopPropagation()} // prevent navigating to detail page
    >
      {/* ── Label + status + details ───────────────────────────────────────── */}
      <div className="flex items-center gap-2 min-w-0">
        <span className={`shrink-0 px-1.5 py-0.5 rounded font-medium ${
          isTimeChange
            ? "bg-blue-100 text-blue-700"
            : "bg-orange-100 text-orange-700"
        }`}>
          {isTimeChange ? "Time change request" : "Cancellation request"}
        </span>
        <span className={`shrink-0 px-1.5 py-0.5 rounded font-medium ${statusStyles[request.status]}`}>
          {statusLabels[request.status]}
        </span>

        {isTimeChange && d && (
          <span className="text-gray-500 truncate">
            {format(parseISO(d.date), "MMM d")}
            {" · "}
            {fmt12h(d.start_time)}–{fmt12h(d.end_time)}
          </span>
        )}

        {isCancellation && (
          <span className="text-gray-400 italic">Partner requested cancellation</span>
        )}
      </div>

      {/* ── Approve / Deny actions — only shown for open requests ────────── */}
      {request.status === "requested" && (
        <div className="flex items-center gap-1.5 shrink-0">
          {approve.isError && (
            <span className="text-red-500 mr-1">Failed</span>
          )}
          <button
            disabled={isPending}
            onClick={() => approve.mutate()}
            className="px-2 py-0.5 rounded font-medium bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 transition-colors"
          >
            {approve.isPending ? "…" : "Approve"}
          </button>
          <button
            disabled={isPending}
            onClick={() => deny.mutate()}
            className="px-2 py-0.5 rounded font-medium bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 transition-colors"
          >
            {deny.isPending ? "…" : "Deny"}
          </button>
        </div>
      )}
    </div>
  );
}
